import { ISO_COUNTRIES } from './iso-countries';

const NAME_TO_ISO2 = new Map(
  ISO_COUNTRIES.map((c) => [c.name.trim().toLowerCase(), c.code]),
);

/**
 * Map a stored country NAME (what the combobox persists, e.g. "United States")
 * to an ISO-2 code for the formatters. Defaults to 'US' for blank/unknown so
 * formatting never breaks on a missing or non-standard value.
 */
export function countryNameToIso2(name: string | undefined): string {
  if (!name) return 'US';
  return NAME_TO_ISO2.get(name.trim().toLowerCase()) ?? 'US';
}

const ISO2_CODES = new Set(ISO_COUNTRIES.map((c) => c.code));
// How the US was commonly typed before the country dropdown (2026-05) — beyond the
// listed "United States" and the code "US".
const US_ALIASES = new Set(['usa', 'u.s.', 'u.s.a.', 'united states of america']);

/**
 * The strict lookup the REGULATORY rules use (U.S. Agent, EU Authorised Representative):
 * a listed name, a 2-letter ISO code ("DE"), or a common way of writing the US — and
 * `null` for anything else (blank, an unlisted native name like "Deutschland").
 * Never guesses: `countryNameToIso2`'s "unknown means US" is right for formatting a
 * postcode, but here it told an EU company saved as "DE" it needed an EU representative.
 * Mirrors backend/services/iso_countries.to_alpha2 (name or code, else unknown).
 */
export function countryToIso2OrNull(value: string | null | undefined): string | null {
  const v = value?.trim();
  if (!v) return null;
  if (v.length === 2 && ISO2_CODES.has(v.toUpperCase())) return v.toUpperCase();
  const lower = v.toLowerCase();
  if (US_ALIASES.has(lower)) return 'US';
  return NAME_TO_ISO2.get(lower) ?? null;
}

/**
 * Resolve the phone-parse country for a field: map the sibling country NAME to
 * ISO-2, or fall back to a market default when the name is blank. (Distinct
 * from `countryNameToIso2`, which defaults blank/unknown to 'US'.)
 */
export function inferPhoneCountry(
  countryName: string | undefined,
  fallbackIso2: string,
): string {
  if (!countryName || !countryName.trim()) return fallbackIso2;
  return countryNameToIso2(countryName);
}
